import type { ReactNode } from "react";
import type { Compendium } from "@lancer-vtt/compendium";
import { HIDDEN_STATUS, OVERSHIELD_RESOURCE, type Combatant, type DisplayCombatant, type EncounterState, type PilotData, disclosureOf, drawnAssetId, isObject, mapAsset } from "@lancer-vtt/domain";
import type { UseEncounter } from "../useEncounter";
import type { UseRoster } from "../../roster/useRemoteRoster";
import { setTrackCommand } from "../tracks";
import { isHpVisible, isMaskedCombatant } from "../disclosure";
import { BaseEditPanel } from "../BaseEditPanel";
import { GmDamageRow } from "../RulingPalette";
import type { ActionDraft } from "../combatInspector";
import { InspectorActionBar } from "./ArmedActionPanel";
import { genericActionBarBlockReason } from "./inspectorEconomy";
import { assertUnmasked, receivesUnmasked } from "./masking";
import { ControlPanel, type ControlPanelSlot } from "./ControlPanel";
import { UnitSheet, UnitSheetStatuses, type UnitSheetIdentity } from "./UnitSheet";
import { GmStrip } from "./GmStrip";
import { UnitSheetVitals } from "./unitVitals";
import { STAT_ABSENT, StatStrip, descriptiveStatCells } from "./unitStats";
import { ConcealToggle, DisclosureControl, DoorToggle, MineDetonateControl } from "./toggles";

/**
 * The **PLAY** inspector for a unit with no sheet of its own: an object (a crate, a wreck, a map
 * asset like a scale tree), a deployable or mine, and a drone. It wears the unit sheet's look
 * ({@link UnitSheet}): the header, the vitals bars, the stat strip, section heads, and for the GM
 * the dark strip at the foot with its trays. What it shows follows what the unit can do:
 *
 * - **Statuses**: the unit sheet's chips row, with the remove `×` and the add form for a viewer who
 *   controls the unit; read-only for anyone else, and absent when there are none to read.
 * - **Actions**: the Skirmish/Activate bar for a drone, or for an object or deployable that carries
 *   a weapon (a turret); a tree or a crate takes no action, so it gets none. The GM's door toggle
 *   and the deployer's DETONATE sit here too. The section is absent when nothing applies.
 * - **GM strip**: REVEAL (the disclosure tier, which decides whether players see the object's HP,
 *   and CONCEAL), DAMAGE (a real damage resolution, which runs armor and destruction), CONTROL
 *   (owner and grants), and MORE (the mid-fight base edit).
 *
 * A player's view of a masked object (disclosure below FULL on an npc-side object) shows the header
 * and a locked note, nothing else.
 */
export function ObjectInspectorBody({
  combatant: c,
  state,
  actorId,
  encounter,
  roster,
  compendium,
  aim,
  setAim,
  onSelfActivate,
  onError,
  error,
  canControl,
  canEditBase,
  canConceal,
  isGm,
  slots,
}: {
  combatant: DisplayCombatant;
  state: EncounterState;
  actorId: string;
  encounter: UseEncounter;
  roster: UseRoster;
  compendium: Compendium | null;
  aim: ActionDraft | null;
  setAim: (a: ActionDraft | null) => void;
  onSelfActivate: (actionId: string) => void;
  onError: (msg: string | null) => void;
  error: string | null;
  canControl: boolean;
  canEditBase: boolean;
  canConceal: boolean;
  isGm: boolean;
  slots: ControlPanelSlot[];
}) {
  const identity = objectSheetIdentity(c);

  // Masked: the tier-1 floor only. No stat fields, no HP, none of the own-unit affordances (a
  // masked combatant is never one this viewer controls).
  if (isMaskedCombatant(c)) {
    return (
      <UnitSheet identity={identity} error={error} className="unit-sheet-object">
        <div className="unit-sheet-object-masked" title="Not yet Scanned">
          STATS UNKNOWN — SCAN OR GM DISCLOSURE REQUIRED
        </div>
      </UnitSheet>
    );
  }

  const setTrack = (kind: "hp" | "heat", value: number) => encounter.dispatch(setTrackCommand({ actorId, combatantId: c.id, kind, value }));
  const cur = c.current;
  // The stat cells the unit has: an object's SPD and E-DEF are a flat 0 it never uses, so they go.
  const stats = descriptiveStatCells(c.base, { grit: false }).filter((s) => s.value !== STAT_ABSENT && !(ZERO_HIDDEN.has(s.label) && s.value === 0));
  // Every own-unit affordance below reads the narrowed combatant, so it needs control AND the
  // unmasked shape: a CTRL grant without EYES (`decisions/0179`) can leave an npc-side drone
  // HP-hidden for its driver, who then gets the read-only sheet.
  const own = canControl && receivesUnmasked(c) ? assertUnmasked(c) : null;
  const armed = own !== null && (!isObject(own) || (own.base.weapons ?? []).length > 0);
  const door = isGm && c.base.objectProfile?.door === true;
  const mine = own !== null && own.mineProfile?.deployedBy !== undefined;

  return (
    <UnitSheet
      identity={identity}
      error={error}
      className="unit-sheet-object"
      gm={isGm ? <ObjectGmStrip combatant={assertUnmasked(c)} actorId={actorId} encounter={encounter} roster={roster} compendium={compendium} slots={slots} canEditBase={canEditBase} canConceal={canConceal} onError={onError} /> : undefined}
    >
      <UnitSheetVitals
        hp={isHpVisible(cur) ? cur.hp : null}
        maxHp={c.base.maxHp}
        heat={c.base.maxHeat !== undefined ? { value: cur.heat ?? 0, max: c.base.maxHeat } : undefined}
        exposed={c.statuses.some((s) => s.name.toLowerCase() === "exposed")}
        shield={cur.resources?.[OVERSHIELD_RESOURCE] ?? 0}
        burn={cur.burn ?? 0}
        onSetTrack={own ? setTrack : null}
        disabled={!actorId}
      />
      {stats.length > 0 && <StatStrip cells={stats} onOpenDice={null} />}

      {(own || c.statuses.length > 0) && (
        <ObjectSection title="STATUSES">
          <UnitSheetStatuses combatant={c} edit={own ? { state, actorId, dispatch: encounter.dispatch } : null} />
        </ObjectSection>
      )}

      {(armed || door || mine) && (
        <ObjectSection title="ACTIONS">
          <div className="unit-sheet-object-actions">
            {/* Door state is GM authoring (`set_door_state` is GM-only at the server gate), so it
                renders on `isGm`, not `canControl`. */}
            {door && <DoorToggle combatant={assertUnmasked(c)} actorId={actorId} dispatch={encounter.dispatch} />}
            {own && <MineDetonateControl combatant={own} sendIntent={encounter.sendIntent} />}
            {/* None of this body's kinds is an actor, so none is ever `"ready"`: a player's press
                meets the room's REJECT arm and only the GM's wave-through resolves. See
                `genericActionBarBlockReason`. */}
            {armed && own && (
              <InspectorActionBar
                combatant={own}
                state={state}
                aim={aim}
                setAim={setAim}
                onSelfActivate={onSelfActivate}
                blockedReason={genericActionBarBlockReason({ state, combatant: own, actorId, isGm })}
              />
            )}
          </div>
        </ObjectSection>
      )}
    </UnitSheet>
  );
}

/** Stat cells left off the strip at 0: an immobile object's speed, and an object's E-Defense. */
const ZERO_HIDDEN = new Set(["SPD", "E-DEF"]);

/** A titled section of the object sheet, headed like the loadout's groups. */
function ObjectSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="unit-sheet-object-section" aria-label={title.toLowerCase()}>
      <div className="unit-sheet-lgroup-head">
        <span className="unit-sheet-lgroup-title">{title}</span>
      </div>
      {children}
    </section>
  );
}

/**
 * The GM strip on an object's sheet: REVEAL, DAMAGE, CONTROL and MORE. There is no RULE tray, since
 * a ruling grants a character a bonus and an object makes no rolls.
 */
function ObjectGmStrip({
  combatant: c,
  actorId,
  encounter,
  roster,
  compendium,
  slots,
  canEditBase,
  canConceal,
  onError,
}: {
  combatant: Combatant;
  actorId: string;
  encounter: UseEncounter;
  roster: UseRoster;
  compendium: Compendium | null;
  slots: ControlPanelSlot[];
  canEditBase: boolean;
  canConceal: boolean;
  onError: (msg: string | null) => void;
}) {
  const owner = c.controllerSlotId ? (slots.find((s) => s.id === c.controllerSlotId)?.name ?? c.controllerSlotId) : "GM";
  const summary = `OWNER ${owner.toUpperCase()} · ${disclosureOf(c).toUpperCase()}`;
  const pilots: PilotData[] = roster.state ? Object.values(roster.state.pilots) : [];
  return (
    <GmStrip
      windowKey={c.id}
      summary={summary}
      trays={{
        ...(canConceal && {
          reveal: (
            <div className="unit-sheet-reveal-tray">
              <DisclosureControl combatant={c} actorId={actorId} dispatch={encounter.dispatch} />
              <ConcealToggle combatant={c} actorId={actorId} dispatch={encounter.dispatch} />
            </div>
          ),
        }),
        damage: <GmDamageRow combatant={c} sendIntent={encounter.sendIntent} />,
        control: <ControlPanel combatant={c} actorId={actorId} slots={slots} dispatch={encounter.dispatch} />,
        ...(canEditBase && {
          more: (
            <div className="unit-sheet-more-tray">
              <BaseEditPanel combatant={c} actorId={actorId} dispatch={encounter.dispatch} rosterDispatch={roster.dispatch} pilots={pilots} compendium={compendium} onError={onError} />
            </div>
          ),
        }),
      }}
    />
  );
}

/** What an object's body is, for the header's neutral tag and the kind line. */
function objectKindLabel(c: DisplayCombatant): string {
  if (c.kind === "drone") return "DRONE";
  if (c.kind === "deployable") return !isMaskedCombatant(c) && c.mineProfile ? "MINE" : "DEPLOYABLE";
  return "OBJECT";
}

/**
 * The sheet header for an object, deployable or drone. A drone is a character, so it keeps the
 * ALLY/HOSTILE tag; an object or deployable is scenery and wears a neutral tag naming its kind
 * instead, the same rule the board's token colour follows. The kind line names its size and, off
 * the object profile, how it affects the map; a map asset's catalogue blurb is the terse line.
 */
export function objectSheetIdentity(c: DisplayCombatant): UnitSheetIdentity {
  const kind = objectKindLabel(c);
  const common = { name: c.name, templates: [], side: c.base.allegiance, hidden: c.statuses.some((s) => s.name === HIDDEN_STATUS) };
  const size = `SIZE ${c.base.size}`;
  if (isMaskedCombatant(c)) return { ...common, kindLine: [size], ...(kind !== "DRONE" && { tag: kind }) };
  const p = c.base.objectProfile;
  const traits = [
    p?.door ? "DOOR" : undefined,
    p?.cover === "hard" ? "HARD COVER" : p?.cover === "soft" ? "SOFT COVER" : undefined,
    p?.blocksMovement ? "BLOCKS MOVEMENT" : p?.moveCost !== undefined ? "DIFFICULT" : undefined,
    p?.blocksSight ? "BLOCKS SIGHT" : undefined,
  ].filter((t): t is string => t !== undefined);
  const assetId = drawnAssetId(c);
  const blurb = assetId !== undefined ? mapAsset(assetId)?.blurb : undefined;
  return {
    ...common,
    kindLine: kind === "DRONE" ? [kind, size, ...traits] : [size, ...traits],
    ...(blurb !== undefined && { terse: blurb }),
    ...(kind !== "DRONE" && { tag: kind }),
  };
}
